import type { ConfigEconomicsItem, ComputedEconomics, EconomicsSettings } from "./types"
import { computeProductEconomics, computeSetEconomics, productRowKey, resolveCogsOverride } from "./economics-calculations"

export interface ProductComputedCacheEntry {
  raw: ConfigEconomicsItem
  settings: EconomicsSettings
  cogs: number | null
  computed: ComputedEconomics
}

export type ProductComputedCache = Map<string, ProductComputedCacheEntry>

/** Cache-backed `computeProductEconomics`: reuses computed ref when inputs match by identity. */
export function getOrComputeProductRow(
  cache: ProductComputedCache,
  raw: ConfigEconomicsItem,
  settings: EconomicsSettings,
  cogs: number | null,
): ComputedEconomics {
  const key = productRowKey(raw)
  const cached = cache.get(key)
  if (cached && cached.raw === raw && cached.settings === settings && cached.cogs === cogs) {
    return cached.computed
  }
  const computed = computeProductEconomics(raw, settings, cogs)
  cache.set(key, { raw, settings, cogs, computed })
  return computed
}

export interface SetComputedCacheEntry {
  cacheKey: string
  settings: EconomicsSettings
  /**
   * Snapshot of member `ConfigEconomicsItem` refs at compute time.
   * Reference-equality check against current members guards against stale
   * cache hits after a period/scope refetch: `rowKey`+`cogs` can stay the same
   * but the inner revenue/order data is different.
   */
  memberRefs: ConfigEconomicsItem[]
  computed: ComputedEconomics
  cogsSubset: Record<string, number>
}

export type SetComputedCache = Map<string, SetComputedCacheEntry>

/**
 * Cache key for a set row: member identities + each member's pending COGS edit.
 * Stable sort so member order in `products` does not invalidate the cache.
 */
export function setCacheKey(
  products: ConfigEconomicsItem[],
  pendingCogs: Readonly<Record<string, number>>,
): string {
  return products
    .map((p) => `${productRowKey(p)}:${resolveCogsOverride(p, pendingCogs) ?? "_"}`)
    .sort()
    .join("|")
}

/** Narrow the pending edits down to members of the set (stable prop for the set row). */
export function buildSetCogsSubset(
  products: ConfigEconomicsItem[],
  pendingCogs: Readonly<Record<string, number>>,
): Record<string, number> {
  const subset: Record<string, number> = {}
  for (const p of products) {
    const v = resolveCogsOverride(p, pendingCogs)
    if (v != null) subset[productRowKey(p)] = v
  }
  return subset
}

function refsMatch<T>(a: readonly T[], b: readonly T[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

/** Cache-backed `computeSetEconomics`: reuses computed + cogsSubset ref on hit. */
export function getOrComputeSet(
  cache: SetComputedCache,
  setId: string,
  products: ConfigEconomicsItem[],
  settings: EconomicsSettings,
  pendingCogs: Readonly<Record<string, number>>,
): { computed: ComputedEconomics; cogsSubset: Record<string, number> } {
  const key = setCacheKey(products, pendingCogs)
  const cached = cache.get(setId)
  if (
    cached
    && cached.cacheKey === key
    && cached.settings === settings
    && refsMatch(cached.memberRefs, products)
  ) {
    return { computed: cached.computed, cogsSubset: cached.cogsSubset }
  }
  const computed = computeSetEconomics(products, settings, pendingCogs)
  const cogsSubset = buildSetCogsSubset(products, pendingCogs)
  cache.set(setId, { cacheKey: key, settings, memberRefs: [...products], computed, cogsSubset })
  return { computed, cogsSubset }
}

/** Evict entries whose keys are no longer present in the current render. */
export function pruneCache<V>(cache: Map<string, V>, activeKeys: Set<string>): void {
  for (const k of cache.keys()) {
    if (!activeKeys.has(k)) cache.delete(k)
  }
}
